import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Section, Button } from "@/components/UI";
import { BeforeAfter } from "@/components/BeforeAfter";
import { Reveal, Overline } from "@/components/Reveal";

export default function CaseDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => { api.get(`/work/${id}`).then((r) => setC(r.data)).catch(() => setNotFound(true)); }, [id]);

  if (notFound) return (
    <Section className="pt-40 pb-32 text-center">
      <p className="text-ccpm-taupe">This case could not be found.</p>
      <Link to="/gallery" className="text-ccpm-olive mt-4 inline-block" data-testid="case-notfound-back">← Back to Our Work</Link>
    </Section>
  );
  if (!c) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-ccpm-olive" /></div>;

  const stages = c.stages || [];

  return (
    <article>
      <div className="pt-32 pb-6 border-b border-ccpm-border bg-ccpm-stone/40">
        <Section><Link to="/gallery" className="inline-flex items-center gap-2 text-sm text-ccpm-olive hover:underline" data-testid="case-back"><ArrowLeft size={16} /> Our Work</Link></Section>
      </div>

      <Section className="pt-14 lg:pt-20 pb-10">
        <Reveal>
          <span className="overline text-ccpm-olive">{c.category}</span>
          <h1 className="font-serif text-4xl lg:text-6xl text-ccpm-charcoal tracking-tight mt-4 leading-[1.03] max-w-4xl" data-testid="case-title">{c.title}</h1>
          {c.description && <p className="mt-5 text-lg text-ccpm-taupe font-light max-w-2xl leading-relaxed">{c.description}</p>}
        </Reveal>
      </Section>

      {(c.before_image || c.after_image) && (
        <Section className="pb-12">
          <Reveal>
            {c.before_image
              ? <BeforeAfter before={c.before_image} after={c.after_image} className="aspect-[16/10] max-h-[70vh]" />
              : <div className="overflow-hidden bg-ccpm-stone"><img src={c.after_image} alt={c.title} className="w-full max-h-[70vh] object-cover" /></div>}
          </Reveal>
        </Section>
      )}

      {c.story && (
        <Section className="pb-16">
          <div className="max-w-2xl">
            {c.story.split("\n").filter((p) => p.trim()).map((p, i) => (
              <p key={i} className="text-ccpm-charcoal/90 leading-[1.9] mb-6 text-[1.05rem] font-light">{p}</p>
            ))}
          </div>
        </Section>
      )}

      {stages.length > 0 && (
        <div className="bg-ccpm-stone py-16 lg:py-24 border-t border-ccpm-border">
          <Section>
            <Reveal><Overline>The journey, step by step</Overline></Reveal>
            <div className="mt-12 space-y-16">
              {stages.map((s, i) => (
                <Reveal key={i} delay={0.04}>
                  <div className={`grid lg:grid-cols-2 gap-8 lg:gap-14 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                    <div className="overflow-hidden bg-ccpm-ivory border border-ccpm-border">
                      <img src={s.image} alt={s.caption || `Stage ${i + 1}`} className="w-full h-full object-cover" data-testid={`case-stage-${i}`} />
                    </div>
                    <div>
                      <span className="font-serif text-5xl text-ccpm-olive/30">{String(i + 1).padStart(2, "0")}</span>
                      {(() => {
                        const cap = s.caption || "";
                        const idx = cap.indexOf(". ");
                        const hasTitle = idx > 0 && cap.slice(0, idx).includes("—");
                        const title = hasTitle ? cap.slice(0, idx) : null;
                        const body = hasTitle ? cap.slice(idx + 2) : cap;
                        return (
                          <>
                            {title && <h3 className="font-serif text-2xl lg:text-[1.9rem] leading-tight text-ccpm-charcoal mt-3">{title}</h3>}
                            {body && <p className="mt-3 text-ccpm-charcoal/90 font-light leading-relaxed text-lg">{body}</p>}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </Section>
        </div>
      )}

      <Section className="py-16 lg:py-24 text-center">
        <p className="text-sm text-ccpm-taupe font-light max-w-2xl mx-auto leading-relaxed mb-8">Shared with the informed consent of the individual pictured. Results vary by person, skin type, and healing.</p>
        <Button to="/gallery" variant="outline" data-testid="case-more">See more of our work</Button>
      </Section>
    </article>
  );
}
